import React, { useEffect, useState } from 'react';
import { useQuery, useMutation, useQueryClient } from 'react-query';
import { toast } from 'react-hot-toast';
import { api } from '../../utils/api';
import { useAuth } from '../../contexts/AuthContext';

const money = (n) => `₹${Number(n || 0).toLocaleString('en-IN')}`;
const dateOf = (v) => (v ? new Date(v).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' }) : '—');

const NUMBER_FIELDS = [
  ['referrerReward', 'Reward for the person who invites (₹)', 'Credited when their friend completes a qualifying first trip.'],
  ['refereeReward', 'Reward for the new customer (₹)', 'Credited to the friend at the same moment.'],
  ['minQualifyingFare', 'Minimum fare of the first trip (₹)', 'A first trip below this earns nothing. Cancellations never count.'],
  ['creditExpiryDays', 'Credit lasts (days)', 'After this an unused credit expires.'],
  ['maxRewardsPerReferrer', 'Most rewards one person can earn', '0 means no limit.'],
  ['maxDiscountPercent', 'A credit can take off at most (% of the trip)', 'Keeps a trip from being free.'],
  ['minFareToUse', 'Minimum trip total to use a credit (₹)', '0 means any trip.'],
  ['newUserWindowDays', 'Code can be entered within (days of joining)', '0 means any time, while the customer has no completed trip.'],
  ['qualifyWithinDays', 'First trip must be within (days of joining)', '0 means no limit.'],
];

const REGION_FIELDS = [
  ['referrerReward', 'Inviter ₹'],
  ['refereeReward', 'New customer ₹'],
  ['minQualifyingFare', 'Min first trip ₹'],
  ['creditExpiryDays', 'Credit days'],
  ['maxDiscountPercent', 'Max %'],
  ['minFareToUse', 'Min trip to use ₹'],
];

// Saved entries <-> the page's rows. A region with no entry follows the program ('same').
const modeOf = (entry) => (!entry ? 'same' : entry.enabled === false ? 'off' : 'custom');

const Stat = ({ label, value, hint }) => (
  <div className="bg-white border border-gray-200 rounded-lg p-4">
    <div className="text-xs text-gray-500">{label}</div>
    <div className="text-2xl font-semibold text-gray-900 mt-1">{value}</div>
    {hint && <div className="text-xs text-gray-400 mt-1">{hint}</div>}
  </div>
);

const Person = ({ p }) => (
  <div>
    <div className="text-sm text-gray-900">{p.name}</div>
    <div className="text-xs text-gray-500">{p.phone}</div>
  </div>
);

const ReferralProgram = () => {
  const { hasPermission } = useAuth();
  const canView = hasPermission('appconfig', 'view');
  const canWrite = hasPermission('appconfig', 'write');
  const qc = useQueryClient();

  const [form, setForm] = useState(null);
  const [status, setStatus] = useState('');
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(1);

  const { data: cfgRes, isLoading } = useQuery(['referral-config'], () => api.get('/referral/config'), { enabled: canView });
  const { data: regionsRes } = useQuery(['referral-regions'], () => api.get('/referral/regions'), { enabled: canView });
  const { data: statsRes } = useQuery(['referral-stats'], () => api.get('/referral/stats'), { enabled: canView });
  const { data: listRes } = useQuery(
    ['referral-list', status, search, page],
    () => api.get('/referral/referrals', { params: { status: status || undefined, search: search || undefined, page, limit: 15 } }),
    { enabled: canView, keepPreviousData: true },
  );

  useEffect(() => {
    const c = cfgRes?.data?.data?.config;
    if (c && !form) setForm(c);
  }, [cfgRes, form]);

  const save = useMutation((payload) => api.put('/referral/config', payload), {
    onSuccess: (res) => {
      toast.success('Referral settings saved');
      setForm(res?.data?.data?.config || form);
      qc.invalidateQueries(['referral-config']);
    },
    onError: (e) => toast.error(e?.response?.data?.message || 'Could not save'),
  });

  if (!canView) return <div className="p-6 text-gray-600">You do not have access to this page.</div>;
  if (isLoading || !form) return <div className="p-6 text-gray-500">Loading…</div>;

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  // Regions that can be given rules: those with an Acting Driver Fare configuration, plus any saved entry
  // whose configuration has since gone, so nothing saved is hidden.
  const known = regionsRes?.data?.data || [];
  const savedRegions = Array.isArray(form.regions) ? form.regions : [];
  const regionRows = [
    ...known,
    ...savedRegions.filter((r) => !known.some((k) => k.regionCode === r.regionCode)).map((r) => ({ regionCode: r.regionCode, name: r.regionCode, isActive: false })),
  ];
  const entryOf = (code) => savedRegions.find((r) => r.regionCode === code);
  const setMode = (code, mode) => setForm((f) => {
    const rest = (f.regions || []).filter((r) => r.regionCode !== code);
    if (mode === 'same') return { ...f, regions: rest };
    const prev = (f.regions || []).find((r) => r.regionCode === code) || {};
    return { ...f, regions: [...rest, { ...prev, regionCode: code, enabled: mode !== 'off' }] };
  });
  const setRegionField = (code, key, value) => setForm((f) => ({
    ...f,
    regions: (f.regions || []).map((r) => (r.regionCode === code ? { ...r, [key]: value } : r)),
  }));
  const stats = statsRes?.data?.data;
  const rows = listRes?.data?.data?.referrals || [];
  const pg = listRes?.data?.data?.pagination;
  const saved = cfgRes?.data?.data?.saved;

  const submit = (e) => {
    e.preventDefault();
    const payload = { ...form };
    NUMBER_FIELDS.forEach(([k]) => { payload[k] = form[k] === '' ? '' : Number(form[k]); });
    payload.regions = (form.regions || []).map((r) => {
      const out = { regionCode: r.regionCode, enabled: r.enabled !== false };
      REGION_FIELDS.forEach(([k]) => { if (r[k] !== undefined && r[k] !== null && r[k] !== '') out[k] = Number(r[k]); });
      return out;
    });
    save.mutate(payload);
  };

  const preview = (form.shareMessage || '')
    .replace(/\{code\}/g, `${form.codePrefix || 'NOT'}K7M2QX`)
    .replace(/\{url\}/g, form.shareUrl || '')
    .replace(/\{refereeReward\}/g, form.refereeReward)
    .replace(/\{referrerReward\}/g, form.referrerReward);

  return (
    <div className="p-6 space-y-6 max-w-6xl">
      <div>
        <h1 className="text-2xl font-semibold text-gray-900">Referral Program</h1>
        <p className="text-sm text-gray-500 mt-1">
          Customers invite friends with a code. When the friend completes a first acting driver trip, both get a credit off their next trip.
          The credit is paid by the platform: the driver still earns the full fare.
        </p>
      </div>

      {stats && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          <Stat label="Friends joined with a code" value={stats.referrals.total} hint={`${stats.referrals.waiting} waiting for a first trip`} />
          <Stat label="Rewarded" value={stats.referrals.rewarded} />
          <Stat label="Credits issued" value={money(stats.credits.issued)} hint={`${stats.credits.issuedCount} credits`} />
          <Stat label="Credits used" value={money(stats.credits.used)} hint={`outstanding ${money(stats.credits.outstanding)} · expired ${money(stats.credits.expired)}`} />
        </div>
      )}

      <form onSubmit={submit} className="bg-white border border-gray-200 rounded-lg p-5 space-y-5">
        <div className="flex items-center justify-between">
          <label className="flex items-center gap-3">
            <input type="checkbox" className="h-5 w-5" disabled={!canWrite} checked={!!form.enabled} onChange={(e) => set('enabled', e.target.checked)} />
            <span>
              <span className="font-medium text-gray-900">Program is {form.enabled ? 'ON' : 'OFF'}</span>
              <span className="block text-xs text-gray-500">
                {saved ? 'While OFF, no codes are shown, no credits are earned and none can be used.' : 'Not saved yet — nothing happens until you save with this switched on.'}
              </span>
            </span>
          </label>
        </div>

        <div className="grid md:grid-cols-2 gap-4">
          {NUMBER_FIELDS.map(([k, label, hint]) => (
            <label key={k} className="block">
              <span className="text-sm font-medium text-gray-700">{label}</span>
              <input
                type="number" min="0" step="1" disabled={!canWrite}
                className="mt-1 w-full border border-gray-300 rounded-md px-3 py-2 text-sm"
                value={form[k] ?? ''} onChange={(e) => set(k, e.target.value)}
              />
              <span className="text-xs text-gray-400">{hint}</span>
            </label>
          ))}
          <label className="block">
            <span className="text-sm font-medium text-gray-700">Code prefix</span>
            <input
              type="text" maxLength={5} disabled={!canWrite}
              className="mt-1 w-full border border-gray-300 rounded-md px-3 py-2 text-sm uppercase"
              value={form.codePrefix ?? ''} onChange={(e) => set('codePrefix', e.target.value.toUpperCase())}
            />
            <span className="text-xs text-gray-400">Codes look like {form.codePrefix || 'NOT'}K7M2QX. Existing codes keep their prefix.</span>
          </label>
        </div>

        <div className="border border-gray-200 rounded-lg p-4 space-y-3">
          <div>
            <h3 className="font-medium text-gray-900">Regions</h3>
            <p className="text-xs text-gray-500">
              The numbers above apply everywhere unless a region says otherwise. A region can be switched off, or given its own amounts
              (leave a box empty to use the number above). The reward follows the region of the new customer&apos;s first trip, and a
              credit can only be used on a booking in a region where the program is on.
            </p>
          </div>
          <label className="flex items-center gap-2 text-sm">
            <input type="checkbox" disabled={!canWrite} checked={form.otherRegionsEnabled !== false} onChange={(e) => set('otherRegionsEnabled', e.target.checked)} />
            <span>Program is on in regions that are not set up below</span>
          </label>
          {regionRows.length === 0 && <p className="text-sm text-gray-400">No regions yet. Add an Acting Driver Fare configuration for a region first.</p>}
          <div className="space-y-3">
            {regionRows.map((r) => {
              const entry = entryOf(r.regionCode);
              const mode = modeOf(entry);
              return (
                <div key={r.regionCode} className="border border-gray-100 rounded-md p-3">
                  <div className="flex flex-wrap items-center justify-between gap-2">
                    <div className="text-sm font-medium text-gray-900">
                      {r.name} <span className="text-xs text-gray-400">{r.regionCode}{r.isActive ? '' : ' · inactive fare config'}</span>
                    </div>
                    <select
                      disabled={!canWrite} value={mode} onChange={(e) => setMode(r.regionCode, e.target.value)}
                      className="border border-gray-300 rounded-md px-3 py-1.5 text-sm"
                    >
                      <option value="same">Same as above</option>
                      <option value="custom">On, with its own amounts</option>
                      <option value="off">Off in this region</option>
                    </select>
                  </div>
                  {mode === 'custom' && (
                    <div className="grid grid-cols-2 md:grid-cols-3 gap-3 mt-3">
                      {REGION_FIELDS.map(([k, label]) => (
                        <label key={k} className="block">
                          <span className="text-xs text-gray-500">{label}</span>
                          <input
                            type="number" min="0" step="1" disabled={!canWrite}
                            placeholder={String(form[k] ?? '')}
                            className="mt-1 w-full border border-gray-300 rounded-md px-2 py-1.5 text-sm"
                            value={entry?.[k] ?? ''} onChange={(e) => setRegionField(r.regionCode, k, e.target.value)}
                          />
                        </label>
                      ))}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </div>

        <label className="block">
          <span className="text-sm font-medium text-gray-700">App link in the share message</span>
          <input
            type="url" disabled={!canWrite} placeholder="https://play.google.com/store/apps/details?id=…"
            className="mt-1 w-full border border-gray-300 rounded-md px-3 py-2 text-sm"
            value={form.shareUrl ?? ''} onChange={(e) => set('shareUrl', e.target.value)}
          />
        </label>

        <label className="block">
          <span className="text-sm font-medium text-gray-700">Share message</span>
          <textarea
            rows={3} maxLength={500} disabled={!canWrite}
            className="mt-1 w-full border border-gray-300 rounded-md px-3 py-2 text-sm"
            value={form.shareMessage ?? ''} onChange={(e) => set('shareMessage', e.target.value)}
          />
          <span className="text-xs text-gray-400">Fills in {'{code}'} (required), {'{url}'}, {'{refereeReward}'} and {'{referrerReward}'}.</span>
          <div className="mt-2 text-sm bg-gray-50 border border-gray-200 rounded-md p-3 text-gray-700">{preview}</div>
        </label>

        <label className="block">
          <span className="text-sm font-medium text-gray-700">Terms shown on the Invite &amp; Earn screen</span>
          <textarea
            rows={4} maxLength={2000} disabled={!canWrite}
            className="mt-1 w-full border border-gray-300 rounded-md px-3 py-2 text-sm"
            value={form.terms ?? ''} onChange={(e) => set('terms', e.target.value)}
          />
        </label>

        {canWrite && (
          <button type="submit" disabled={save.isLoading} className="bg-blue-600 text-white px-5 py-2 rounded-md text-sm font-medium disabled:opacity-50">
            {save.isLoading ? 'Saving…' : 'Save settings'}
          </button>
        )}
      </form>

      {stats?.topReferrers?.length > 0 && (
        <div className="bg-white border border-gray-200 rounded-lg p-5">
          <h2 className="font-medium text-gray-900 mb-3">Top referrers</h2>
          <div className="divide-y">
            {stats.topReferrers.map((r) => (
              <div key={r.id} className="py-2 flex justify-between text-sm">
                <span>{r.name} <span className="text-gray-400">{r.phone} · {r.code}</span></span>
                <span className="font-medium">{r.rewarded} rewarded</span>
              </div>
            ))}
          </div>
        </div>
      )}

      <div className="bg-white border border-gray-200 rounded-lg p-5">
        <div className="flex flex-wrap gap-3 items-center justify-between mb-3">
          <h2 className="font-medium text-gray-900">Referrals</h2>
          <div className="flex gap-2">
            <input
              type="text" placeholder="Search name, phone or code" value={search}
              onChange={(e) => { setSearch(e.target.value); setPage(1); }}
              className="border border-gray-300 rounded-md px-3 py-1.5 text-sm"
            />
            <select value={status} onChange={(e) => { setStatus(e.target.value); setPage(1); }} className="border border-gray-300 rounded-md px-3 py-1.5 text-sm">
              <option value="">All</option>
              <option value="SIGNED_UP">Waiting for first trip</option>
              <option value="REWARDED">Rewarded</option>
            </select>
          </div>
        </div>

        <div className="overflow-x-auto">
          <table className="w-full text-left">
            <thead className="text-xs text-gray-500 border-b">
              <tr>
                <th className="py-2 pr-4">Invited by</th><th className="pr-4">Friend</th><th className="pr-4">Code</th>
                <th className="pr-4">Joined</th><th className="pr-4">Status</th><th>First trip</th>
              </tr>
            </thead>
            <tbody className="divide-y">
              {rows.length === 0 && <tr><td colSpan={6} className="py-6 text-center text-sm text-gray-400">No referrals yet</td></tr>}
              {rows.map((r) => (
                <tr key={r.id}>
                  <td className="py-2 pr-4"><Person p={r.referrer} /></td>
                  <td className="pr-4"><Person p={r.referee} /></td>
                  <td className="pr-4 text-sm">{r.code}</td>
                  <td className="pr-4 text-sm">{dateOf(r.joinedAt)}</td>
                  <td className="pr-4 text-sm">
                    {r.status === 'REWARDED' ? 'Rewarded' : 'Waiting'}
                    {r.referrerCapped && <span className="block text-xs text-amber-600">inviter at limit</span>}
                  </td>
                  <td className="text-sm">{r.qualifyingFare ? `${money(r.qualifyingFare)} · ${dateOf(r.rewardedAt)}` : '—'}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {pg && pg.totalPages > 1 && (
          <div className="flex items-center justify-between mt-3 text-sm">
            <button disabled={page <= 1} onClick={() => setPage(page - 1)} className="px-3 py-1 border rounded disabled:opacity-40">Previous</button>
            <span className="text-gray-500">Page {pg.page} of {pg.totalPages}</span>
            <button disabled={page >= pg.totalPages} onClick={() => setPage(page + 1)} className="px-3 py-1 border rounded disabled:opacity-40">Next</button>
          </div>
        )}
      </div>
    </div>
  );
};

export default ReferralProgram;
